import {
  DynamicSelectQuestion,
  MultiChoiceAnswer,
  MultiChoiceQuestion,
  OnboardingAnswerValue,
  OnboardingQuestion,
  SingleChoiceAnswer,
  SingleChoiceQuestion,
  SliderQuestion,
  TextQuestion,
} from "../types";
import { validateTextRule } from "./fieldValidation";

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const toSingleChoiceAnswer = (
  value: OnboardingAnswerValue | undefined
): SingleChoiceAnswer | undefined => {
  if (!value || !isRecord(value)) {
    return undefined;
  }

  if (typeof value.choice !== "string") {
    return undefined;
  }

  return {
    choice: value.choice,
    customText:
      "customText" in value && typeof value.customText === "string"
        ? value.customText
        : undefined,
  };
};

export const toMultiChoiceAnswer = (
  value: OnboardingAnswerValue | undefined
): MultiChoiceAnswer | undefined => {
  if (!value || !isRecord(value)) {
    return undefined;
  }

  if (!Array.isArray(value.choices)) {
    return undefined;
  }

  return {
    choices: value.choices as string[],
    customText:
      "customText" in value && typeof value.customText === "string"
        ? value.customText
        : undefined,
  };
};

export const ensureSliderValue = (
  question: SliderQuestion,
  value: OnboardingAnswerValue | undefined
): number | string => {
  if (typeof value === "string" && value === "prefer_not_to_say") {
    return value;
  }

  if (typeof value === "number") {
    return value;
  }

  if (question.defaultValue !== undefined) {
    return question.defaultValue;
  }

  return Math.round((question.min + question.max) / 2);
};

export const isQuestionAnswered = (
  question: OnboardingQuestion,
  value: OnboardingAnswerValue | undefined
): boolean => {
  // Format rules apply even to optional text fields: a typed value must be
  // well-formed before the user can proceed.
  if (question.type === "text") {
    if (validateTextRule((question as TextQuestion).validation, value) !== null) {
      return false;
    }
  }

  if (question.optional) {
    return true;
  }

  switch (question.type) {
    case "single": {
      const answer = toSingleChoiceAnswer(value);
      if (!answer || !answer.choice) {
        return false;
      }
      if (
        question.customOption &&
        answer.choice === question.customOption.value
      ) {
        return Boolean(answer.customText && answer.customText.trim().length);
      }
      return true;
    }
    case "multi": {
      const answer = toMultiChoiceAnswer(value);
      if (!answer || answer.choices.length === 0) {
        return false;
      }
      if (
        question.customOption &&
        answer.choices.includes(question.customOption.value)
      ) {
        return Boolean(answer.customText && answer.customText.trim().length);
      }
      return true;
    }
    case "slider":
      return (
        typeof value === "number" ||
        (typeof value === "string" && value === "prefer_not_to_say")
      );
    case "text":
      return typeof value === "string" && value.trim().length > 0;
    case "datepicker":
      return typeof value === "string" && value.trim().length > 0;
    case "qrcode":
      return typeof value === "string" && value.trim().length > 0;
    case "dynamic_select":
      return typeof value === "string" && value.trim().length > 0;
    default:
      return false;
  }
};

const findChoiceLabel = (
  question: SingleChoiceQuestion | MultiChoiceQuestion,
  value: string
) => question.options.find((option) => option.value === value)?.label ?? value;

export type FormattedAnswer =
  | { kind: "text"; value: string }
  | { kind: "list"; value: string[] };

export const formatAnswerForSummary = (
  question: OnboardingQuestion,
  value: OnboardingAnswerValue | undefined
): FormattedAnswer | undefined | null => {
  if (value === undefined || value === null) {
    return question.optional ? null : undefined;
  }

  switch (question.type) {
    case "single": {
      const answer = toSingleChoiceAnswer(value);
      if (!answer) {
        if (typeof value === "string") {
          return { kind: "text", value: findChoiceLabel(question, value) };
        }
        return undefined;
      }

      if (
        question.customOption &&
        answer.choice === question.customOption.value
      ) {
        if (answer.customText && answer.customText.trim().length) {
          return { kind: "text", value: answer.customText.trim() };
        }
        return { kind: "text", value: question.customOption.label };
      }

      return { kind: "text", value: findChoiceLabel(question, answer.choice) };
    }
    case "multi": {
      const answer = toMultiChoiceAnswer(value);
      const selections = answer
        ? answer.choices
        : Array.isArray(value)
        ? value
        : [];

      if (!selections.length) {
        return question.optional ? null : undefined;
      }

      const formatted = selections.filter(Boolean).map((selection) => {
        if (
          question.customOption &&
          selection === question.customOption.value
        ) {
          if (answer?.customText && answer.customText.trim().length) {
            return answer.customText.trim();
          }
          return question.customOption.label;
        }
        return findChoiceLabel(question, selection);
      });

      return { kind: "list", value: formatted };
    }
    case "slider":
      if (typeof value === "string" && value === "prefer_not_to_say") {
        return { kind: "text", value: "Prefer not to say" };
      }
      return typeof value === "number"
        ? {
            kind: "text",
            value: `${value}${question.unit ? ` ${question.unit}` : ""}`,
          }
        : undefined;
    case "text": {
      if (typeof value !== "string") {
        return undefined;
      }
      const trimmed = value.trim();
      if (!trimmed.length) {
        return question.optional ? null : undefined;
      }
      return { kind: "text", value: trimmed };
    }
    case "datepicker": {
      if (typeof value !== "string") {
        return undefined;
      }
      const trimmed = value.trim();
      if (!trimmed.length) {
        return question.optional ? null : undefined;
      }
      return { kind: "text", value: trimmed };
    }
    case "qrcode": {
      if (typeof value !== "string") {
        return undefined;
      }
      const trimmed = value.trim();
      if (!trimmed.length) {
        return question.optional ? null : undefined;
      }
      return { kind: "text", value: trimmed };
    }
    case "dynamic_select": {
      if (typeof value !== "string") {
        return undefined;
      }
      const trimmed = value.trim();
      if (!trimmed.length) {
        return question.optional ? null : undefined;
      }
      const label =
        (question as DynamicSelectQuestion).options.find(
          (option) => option.value === trimmed
        )?.label ?? trimmed;
      return { kind: "text", value: label };
    }
    default:
      return undefined;
  }
};
